import { useQuery } from '@apollo/client/react';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import { Candidate } from '@pflegia/data-access';
import { CandidateDocument } from '@pflegia/data-access/candidate.generated';
import { RegisterMethod } from '@pflegia/types/common/enums/register-method.enum';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { useCollectInfoModalStore } from '@pflegia/ui/stores/use-collect-info-modal.store';

import { getFiltersValueFromCandidate } from '@domains/job-board/application/use-filter-state.hook';
import { useFiltersLocalStorage } from '@domains/job-board/application/use-filters-local-storage';

import { MAINTENANCE } from '@shared/application/constants';

export const useCollectInfoModal = ({ showOnceAfterLogin = false } = {}) => {
  const authenticated = useAuth();
  const { pathname } = useRouter();
  const { modalShown, setModalShown } = useCollectInfoModalStore();
  const { setFilters } = useFiltersLocalStorage();

  const { data, refetch } = useQuery(CandidateDocument, {
    skip: !authenticated || pathname === MAINTENANCE,
    fetchPolicy: 'network-only',
  });
  const candidate = data?.candidate as Candidate;

  const shouldShowCollectInfoModal = useMemo(() => {
    if (!candidate) return;

    const { typesOfWork, germanSpeakingLevel, nursingApprenticeshipExperience, driversLicense, registerMethod, permanentOrTemporary } =
      candidate;

    const hasMissingFields =
      !typesOfWork?.length || !germanSpeakingLevel || !nursingApprenticeshipExperience || !driversLicense || !permanentOrTemporary;
    const hasValidRegisterMethod = [RegisterMethod.Multiposting, RegisterMethod.QuickSignup, RegisterMethod.Transparent].includes(
      registerMethod as RegisterMethod,
    );

    return hasMissingFields && hasValidRegisterMethod;
  }, [candidate]);

  const updateFilters = useCallback(async () => {
    const { data: refreshedData } = await refetch();
    if (refreshedData?.candidate) setFilters(getFiltersValueFromCandidate(refreshedData.candidate));
  }, [refetch, setFilters]);

  const handleModalClose = useCallback(async () => {
    await updateFilters();
    setModalShown(true);
  }, [setModalShown, updateFilters]);

  return {
    candidate,
    modalShown,
    setModalShown,
    showModal: shouldShowCollectInfoModal && (!showOnceAfterLogin || !modalShown),
    updateFilters,
    handleModalClose,
  };
};
